import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import keys from 'lodash/keys';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { isValidElementType } from 'react-is';
import { SpecTypes } from '../../../constants';
import { isArraySpec, isCorrectSpec, isNumberSpec, isObjectSpec } from '../../../helpers';
import { EMPTY_MUTATOR, OBJECT_ARRAY_CNT, OBJECT_ARRAY_FLAG } from '../constants';
import { isArrayItem, isCorrectConfig, transformArrIn, transformArrOut } from '../utils';
const isErrorMutatorCorrect = (errorMutator) => errorMutator !== EMPTY_MUTATOR &&
    (isString(errorMutator.value) ||
        isBoolean(errorMutator.value) ||
        isUndefined(errorMutator.value));
const isValueMutatorCorrect = (valueMutator, spec) => valueMutator !== EMPTY_MUTATOR &&
    (typeof valueMutator.value === spec.type ||
        (isArray(valueMutator.value) && spec.type === SpecTypes.Array) ||
        valueMutator.value === undefined);
export const updateParentStore = (store) => {
    (store.parentOnChange ? store.parentOnChange : store.tools.onChange)(store.name, store.state.value, Object.assign(Object.assign({}, store.state.childErrors), { [store.name]: store.state.error }));
};
export const callUnmout = (store) => {
    (store.parentOnUnmount ? store.parentOnUnmount : store.tools.onUnmount)(store.name);
};
export const getSpec = ({ name, spec, mutatorsStore, }) => {
    const mutator = get(mutatorsStore.spec, name, EMPTY_MUTATOR);
    if (mutator !== EMPTY_MUTATOR) {
        const mutatedSpec = merge(cloneDeep(spec), mutator.value);
        if (isCorrectSpec(mutatedSpec)) {
            return mutatedSpec;
        }
    }
    return spec;
};
export const getComponents = ({ spec, config, }) => {
    const result = {};
    if (isCorrectConfig(config) && isCorrectSpec(spec)) {
        const { inputs, layouts } = config[spec.type];
        if (inputs) {
            const entity = inputs[spec.viewSpec.type];
            if (isValidElementType(entity === null || entity === void 0 ? void 0 : entity.Component)) {
                result.inputEntity = entity;
            }
        }
        if (layouts && isString(spec.viewSpec.layout)) {
            const Component = layouts[spec.viewSpec.layout];
            if (isValidElementType(Component)) {
                result.Layout = Component;
            }
        }
    }
    return result;
};
export const getRender = ({ name, spec, inputEntity, Layout, }) => {
    const render = (props) => {
        if (inputEntity && isCorrectSpec(spec) && isString(name)) {
            if (!spec.viewSpec.hidden) {
                const { layoutProps, inputProps } = spec.viewSpec;
                if (inputEntity.independent) {
                    const InputComponent = inputEntity.Component;
                    return (React.createElement(InputComponent, Object.assign({ spec: spec, name: name, Layout: Layout, inputProps: inputProps, layoutProps: layoutProps }, props)));
                }
                const InputComponent = inputEntity.Component;
                const input = (React.createElement(InputComponent, Object.assign({ spec: spec, name: name, inputProps: inputProps }, props)));
                if (Layout) {
                    return (React.createElement(Layout, Object.assign({ spec: spec, name: name, layoutProps: layoutProps }, props), input));
                }
                return input;
            }
        }
        return null;
    };
    return render;
};
export const getValidate = ({ spec, config, }) => {
    let validate = () => undefined;
    if (isCorrectConfig(config) && isCorrectSpec(spec) && !spec.viewSpec.hidden) {
        const { validators } = config[spec.type];
        if (validators) {
            if ((!isString(spec.validator) || !spec.validator.length) &&
                isFunction(validators.base)) {
                validate = (value) => validators.base(spec, value);
            }
            if (isString(spec.validator) && isFunction(validators[spec.validator])) {
                validate = (value) => validators[spec.validator](spec, value);
            }
        }
    }
    return validate;
};
export const getFieldInitials = ({ name, spec, valueFromParent, initialValue, validate, mutatorsStore, }) => {
    const valueMutator = transformArrIn(get(mutatorsStore.values, name, EMPTY_MUTATOR));
    let value = cloneDeep(valueFromParent);
    if (isValueMutatorCorrect(valueMutator, spec)) {
        value = valueMutator.value;
    }
    if (isNil(value)) {
        if (spec.defaultValue) {
            value = transformArrIn(spec.defaultValue);
        }
        // if the spec with type array or object, and this spec has "required === true",
        // we immediately exclude empty value
        else if (spec.required) {
            if (isArraySpec(spec)) {
                value = { [OBJECT_ARRAY_FLAG]: true, [OBJECT_ARRAY_CNT]: 0 };
            }
            else if (isObjectSpec(spec)) {
                value = {};
            }
        }
    }
    let errorMutator = get(mutatorsStore.errors, name, EMPTY_MUTATOR);
    if (!isErrorMutatorCorrect(errorMutator)) {
        errorMutator = { value: undefined };
    }
    const error = (validate === null || validate === void 0 ? void 0 : validate(transformArrOut(value))) || (errorMutator === null || errorMutator === void 0 ? void 0 : errorMutator.value);
    const dirty = !isEqual(value, initialValue);
    return {
        initialValue,
        active: false,
        dirty,
        error,
        invalid: Boolean(error),
        modified: dirty,
        pristine: true,
        touched: false,
        valid: !error,
        value,
        visited: false,
        childErrors: {},
    };
};
export const getFieldMethods = () => {
    const onChange = (store, { valOrSetter, childErrors, errorMutator }) => {
        const { state, validate, spec } = store;
        const _value = isFunction(valOrSetter) ? valOrSetter(state.value) : valOrSetter;
        const error = (validate === null || validate === void 0 ? void 0 : validate(transformArrOut(_value))) || errorMutator;
        let value = transformArrIn(_value);
        if (isNumberSpec(spec) && !error) {
            value = (value ? Number(value) : undefined);
        }
        let newChildErrors = Object.assign({}, state.childErrors);
        if (childErrors) {
            const nearestChildName = keys(childErrors).sort((a, b) => a.length - b.length)[0];
            if (nearestChildName) {
                const existingСhildNames = keys(newChildErrors).filter((childName) => childName.startsWith(nearestChildName));
                newChildErrors = Object.assign(Object.assign({}, omit(newChildErrors, existingСhildNames)), childErrors);
            }
        }
        const nextStore = Object.assign(Object.assign({}, store), { state: Object.assign(Object.assign({}, store.state), { dirty: !isEqual(value, state.initialValue), error, invalid: Boolean(error), modified: true, pristine: value === state.initialValue, touched: true, valid: !error, value, visited: true, childErrors: newChildErrors }) });
        nextStore.afterStoreUpdateCB = () => updateParentStore(nextStore);
        return nextStore;
    };
    const onDrop = (store) => {
        const { name } = store;
        if (isArrayItem(name)) {
            const afterStoreUpdateCB = () => callUnmout(store);
            return Object.assign(Object.assign({}, store), { afterStoreUpdateCB });
        }
        return onChange(store, {
            valOrSetter: undefined,
            childErrors: { [name]: false },
        });
    };
    const onBlur = (store) => {
        return Object.assign(Object.assign({}, store), { state: Object.assign(Object.assign({}, store.state), { active: false, touched: true }) });
    };
    const onFocus = (store) => {
        return Object.assign(Object.assign({}, store), { state: Object.assign(Object.assign({}, store.state), { active: true, visited: true }) });
    };
    const parentOnUnmount = (store, childName) => {
        const { name, spec } = store;
        if (isArraySpec(spec) || isObjectSpec(spec)) {
            return onChange(store, {
                valOrSetter: (currentValue) => currentValue
                    ? omit(currentValue, childName.split(`${name}.`)[1])
                    : currentValue,
                childErrors: { [childName]: false },
            });
        }
        return store;
    };
    const onItemAdd = (store, _value) => {
        const { validate, state } = store;
        const stateValue = (state.value || {
            [OBJECT_ARRAY_FLAG]: true,
            [OBJECT_ARRAY_CNT]: 0,
        });
        const value = Object.assign(Object.assign({}, stateValue), { [`<${stateValue[OBJECT_ARRAY_CNT]}>`]: transformArrIn(_value), [OBJECT_ARRAY_CNT]: stateValue[OBJECT_ARRAY_CNT] + 1 });
        const error = validate === null || validate === void 0 ? void 0 : validate(transformArrOut(value));
        const nextStore = Object.assign(Object.assign({}, store), { state: Object.assign(Object.assign({}, store.state), { dirty: !isEqual(value, store.state.initialValue), error, invalid: Boolean(error), modified: true, pristine: value === store.state.initialValue, touched: true, valid: !error, value, visited: true }) });
        nextStore.afterStoreUpdateCB = () => updateParentStore(nextStore);
        return nextStore;
    };
    const onItemRemove = (store, idx) => {
        return parentOnUnmount(store, `${store.name}.<${idx}>`);
    };
    return {
        onChange,
        onDrop,
        onBlur,
        onFocus,
        parentOnUnmount,
        onItemAdd,
        onItemRemove,
    };
};
export const initializeStore = ({ name, spec: _spec, mutatorsStore, config, valueFromParent, tools, parentOnChange, parentOnUnmount, }) => {
    const spec = getSpec({ name, spec: _spec, mutatorsStore });
    const components = getComponents({ spec, config });
    const render = getRender(Object.assign({ name, spec }, components));
    const validate = getValidate({ spec, config });
    const state = getFieldInitials({
        name,
        spec,
        valueFromParent,
        validate,
        mutatorsStore,
        initialValue: get(tools.initialValue, name),
    });
    const initialsStore = {
        name,
        spec,
        initialSpec: _spec,
        config,
        tools,
        mutatorsStore,
        render,
        validate,
        parentOnChange,
        parentOnUnmount,
        state,
    };
    if (!isEqual(valueFromParent, state.value) || state.error) {
        initialsStore.afterStoreUpdateCB = () => updateParentStore(initialsStore);
    }
    return initialsStore;
};
export const updateStore = ({ store, setStore, spec: _spec, name, parentOnChange, parentOnUnmount, mutatorsStore, valueFromParent, config, tools, methodOnChange, }) => {
    const storeSpecMutator = get(store.mutatorsStore.spec, store.name, EMPTY_MUTATOR);
    const storeValueMutator = get(store.mutatorsStore.values, store.name, EMPTY_MUTATOR);
    const storeErrorMutator = get(store.mutatorsStore.errors, store.name, EMPTY_MUTATOR);
    const specMutator = get(mutatorsStore.spec, name, EMPTY_MUTATOR);
    const valueMutator = get(mutatorsStore.values, name, EMPTY_MUTATOR);
    const errorMutator = get(mutatorsStore.errors, name, EMPTY_MUTATOR);
    const valueMutatorUpdated = isValueMutatorCorrect(valueMutator, getSpec({ name, spec: _spec, mutatorsStore })) &&
        valueMutator !== storeValueMutator;
    const errorMutatorUpdated = isErrorMutatorCorrect(errorMutator) && errorMutator !== storeErrorMutator;
    const updateState = valueMutatorUpdated || errorMutatorUpdated;
    const updateNonCritical = parentOnChange !== store.parentOnChange ||
        parentOnUnmount !== store.parentOnUnmount ||
        tools.onChange !== store.tools.onChange ||
        tools.onUnmount !== store.tools.onUnmount;
    const updateAllStore = !isEqual(_spec, store.initialSpec) ||
        config !== store.config ||
        (specMutator !== EMPTY_MUTATOR && specMutator !== storeSpecMutator);
    const updateAllStoreAndClearParentValues = name !== store.name;
    if (updateAllStoreAndClearParentValues) {
        callUnmout(store);
        setStore(initializeStore({
            name,
            spec: _spec,
            mutatorsStore,
            config,
            valueFromParent,
            tools,
            parentOnChange,
            parentOnUnmount,
        }));
    }
    else if (updateAllStore) {
        let nextStore = Object.assign(Object.assign({}, initializeStore({
            name,
            spec: _spec,
            mutatorsStore,
            config,
            valueFromParent,
            tools,
            parentOnChange,
            parentOnUnmount,
        })), { state: store.state });
        nextStore = methodOnChange(nextStore, Object.assign({ valOrSetter: (value) => valueMutatorUpdated ? valueMutator.value : value }, (errorMutatorUpdated
            ? { errorMutator: errorMutator.value }
            : {})));
        setStore(nextStore);
    }
    else if (updateNonCritical) {
        let nextStore = Object.assign(Object.assign({}, store), { parentOnChange,
            parentOnUnmount,
            tools });
        if (updateState) {
            nextStore = methodOnChange(nextStore, Object.assign({ valOrSetter: (value) => valueMutatorUpdated ? valueMutator.value : value }, (errorMutatorUpdated
                ? { errorMutator: errorMutator.value }
                : {})));
        }
        setStore(nextStore);
    }
    else if (updateState) {
        setStore(methodOnChange(Object.assign(Object.assign({}, store), { mutatorsStore }), Object.assign({ valOrSetter: (value) => valueMutatorUpdated ? valueMutator.value : value }, (errorMutatorUpdated
            ? { errorMutator: errorMutator.value }
            : {}))));
    }
};
